import React, { useMemo, useCallback } from 'react';
import { TableHead, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Search,
  Loader2,
  X,
  Filter,
  Calendar,
  DollarSign,
  Package,
  Building2,
  Layers3,
  Hash,
  Type,
  RotateCcw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

import { type Table } from '@tanstack/react-table';
import { type Product } from '@/services/productService';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { formatDateForDisplay, parseNumberFromInput, NumberFormat } from '@/utils/numberFormat';
import { Card, CardContent } from '@/components/ui/card';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { useProductstableTranslations } from '@/hooks/useProductstableTranslations';
import { logError } from '@/utils/errorHandler';
import { ProductsFilterState } from '@/types/filters';
import {
  getCurrentStatusValue,
  isValidStatusValue,
  debugStatusFilter,
} from '@/utils/statusFilterUtils';

interface ProductsTableFiltersProps {
  columns: any[];
  filters: Record<string, any> | ProductsFilterState; // Accept both for compatibility
  onFilterChange: (columnId: string, value: any) => void;
  onClearFilters: () => void;
  table: Table<Product> | null;
  uniqueCategories: { label: string; value: string | number }[];
  uniqueTags: string[];
  uniqueBrands: string[];
  families: any[];
  isFamiliesLoading: boolean;
}

/**
 * A component that renders filter controls for the products table
 */
export function ProductsTableFilters({
  columns: _columns,
  filters,
  onFilterChange,
  onClearFilters,
  table,
  uniqueCategories,
  uniqueTags,
  uniqueBrands,
  families,
  isFamiliesLoading,
}: ProductsTableFiltersProps) {
  // i18n Step 1: Initialize translation hook
  const i18n = useProductstableTranslations();

  // i18n Step 1: Create stable memoized translations to prevent re-renders
  const translations = useMemo(
    () => ({
      title: i18n.getFiltersText('title'),
      noFiltersApplied: i18n.getFiltersText('noFiltersApplied'),
      buttons: {
        resetAll: i18n.getFiltersButtonText('resetAll'),
        expand: i18n.getFiltersButtonText('expand'),
        collapse: i18n.getFiltersButtonText('collapse'),
      },
      labels: {
        sku: i18n.getFiltersLabelText('sku'),
        productName: i18n.getFiltersLabelText('productName'),
        family: i18n.getFiltersLabelText('family'),
        category: i18n.getFiltersLabelText('category'),
        brand: i18n.getFiltersLabelText('brand'),
        tags: i18n.getFiltersLabelText('tags'),
        barcode: i18n.getFiltersLabelText('barcode'),
        price: i18n.getFiltersLabelText('price'),
        status: i18n.getFiltersLabelText('status'),
        createdAt: i18n.getFiltersLabelText('createdAt'),
        updatedAt: i18n.getFiltersLabelText('updatedAt'),
      },
      placeholders: {
        enterSku: i18n.getFiltersPlaceholderText('enterSku'),
        enterProductName: i18n.getFiltersPlaceholderText('enterProductName'),
        enterBarcode: i18n.getFiltersPlaceholderText('enterBarcode'),
        searchFamilies: i18n.getFiltersPlaceholderText('searchFamilies'),
        searchCategories: i18n.getFiltersPlaceholderText('searchCategories'),
        searchBrands: i18n.getFiltersPlaceholderText('searchBrands'),
        searchTags: i18n.getFiltersPlaceholderText('searchTags'),
        selectStartDate: i18n.getFiltersPlaceholderText('selectStartDate'),
        selectEndDate: i18n.getFiltersPlaceholderText('selectEndDate'),
        minPrice: i18n.getFiltersPlaceholderText('minPrice'),
        maxPrice: i18n.getFiltersPlaceholderText('maxPrice'),
      },
      options: {
        allFamilies: i18n.getFiltersOptionText('allFamilies'),
        allCategories: i18n.getFiltersOptionText('allCategories'),
        allBrands: i18n.getFiltersOptionText('allBrands'),
        allTags: i18n.getFiltersOptionText('allTags'),
        allStatuses: i18n.getFiltersOptionText('allStatuses'),
        anyPrice: i18n.getFiltersOptionText('anyPrice'),
        active: i18n.getFiltersOptionText('active'),
        inactive: i18n.getFiltersOptionText('inactive'),
      },
      messages: {
        loadingFamilies: i18n.getFiltersMessageText('loadingFamilies'),
        loadingCategories: i18n.getFiltersMessageText('loadingCategories'),
        loadingBrands: i18n.getFiltersMessageText('loadingBrands'),
        loadingTags: i18n.getFiltersMessageText('loadingTags'),
        noFamiliesAvailable: i18n.getFiltersMessageText('noFamiliesAvailable'),
        noCategoriesAvailable: i18n.getFiltersMessageText('noCategoriesAvailable'),
        noBrandsAvailable: i18n.getFiltersMessageText('noBrandsAvailable'),
        noTagsAvailable: i18n.getFiltersMessageText('noTagsAvailable'),
        priceRangeInvalid: i18n.getFiltersMessageText('priceRangeInvalid'),
      },
    }),
    [i18n]
  );

  // i18n Step 1: Helper function for filters applied text with pluralization
  const getFiltersAppliedText = useCallback(
    (count: number) => {
      if (count === 0) {
        return translations.noFiltersApplied;
      }
      const plural = count === 1 ? '' : 's';
      return i18n.getFiltersText('filtersApplied', { count, plural });
    },
    [i18n, translations.noFiltersApplied]
  );

  // i18n Step 1: Helper functions for search messages
  const getNoFamiliesFoundText = useCallback(
    (search: string) => {
      return i18n.getFiltersMessageText('noFamiliesFound', { search });
    },
    [i18n]
  );

  const getNoCategoriesFoundText = useCallback(
    (search: string) => {
      return i18n.getFiltersMessageText('noCategoriesFound', { search });
    },
    [i18n]
  );

  const getNoBrandsFoundText = useCallback(
    (search: string) => {
      return i18n.getFiltersMessageText('noBrandsFound', { search });
    },
    [i18n]
  );

  const getNoTagsFoundText = useCallback(
    (search: string) => {
      return i18n.getFiltersMessageText('noTagsFound', { search });
    },
    [i18n]
  );

  // Get organization settings for number formatting
  const { orgSettings } = useOrgSettings({ includePricing: false });
  const numberFormat = (orgSettings?.number_format || 'dot') as NumberFormat;

  // Add state for search inputs in all dropdowns
  const [tagSearch, setTagSearch] = React.useState('');
  const [brandSearch, setBrandSearch] = React.useState('');
  const [familySearch, setFamilySearch] = React.useState('');
  const [categorySearch, setCategorySearch] = React.useState('');

  // Add state for collapse/expand functionality
  const [isCollapsed, setIsCollapsed] = React.useState(true);

  // Format price values for display in inputs (convert from API dot format to org format)
  const formatPriceForInput = useCallback(
    (value: string | undefined) => {
      if (!value) return '';

      const numericValue = parseFloat(value);
      if (isNaN(numericValue)) return '';

      // Format according to organization preferences
      if (numberFormat === 'comma') {
        return numericValue.toFixed(2).replace('.', ',');
      } else {
        return numericValue.toFixed(2);
      }
    },
    [numberFormat]
  );

  // Add local state for price inputs to allow free typing
  const [localMinPrice, setLocalMinPrice] = React.useState('');
  const [localMaxPrice, setLocalMaxPrice] = React.useState('');

  // Sync local price states with filter values when filters change
  React.useEffect(() => {
    setLocalMinPrice(filters.minPrice ? formatPriceForInput(filters.minPrice) : '');
  }, [filters.minPrice, formatPriceForInput]);

  React.useEffect(() => {
    setLocalMaxPrice(filters.maxPrice ? formatPriceForInput(filters.maxPrice) : '');
  }, [filters.maxPrice, formatPriceForInput]);

  // Check if we're in grid view mode (when table is null)
  const isGridView = table === null;

  // Helper functions for price input formatting
  const getPricePlaceholder = () => {
    return numberFormat === 'comma'
      ? i18n.getFiltersPriceFormattingText('defaultValueComma')
      : i18n.getFiltersPriceFormattingText('defaultValue');
  };

  // Handle price input validation on blur (when user finishes typing)
  const handlePriceInputBlur = (value: string, filterKey: 'minPrice' | 'maxPrice') => {
    if (value === '') {
      onFilterChange(filterKey, undefined);
      return;
    }

    // Parse the input according to organization format
    const parsedValue = parseNumberFromInput(value, numberFormat);

    // Only update if it's a valid positive number
    if (!isNaN(parsedValue) && parsedValue >= 0) {
      // Convert to string for API (always dot format for backend)
      const apiValue = parsedValue.toString();
      onFilterChange(filterKey, apiValue);
    } else {
      // Invalid input - reset to previous value
      if (filterKey === 'minPrice') {
        setLocalMinPrice(filters.minPrice ? formatPriceForInput(filters.minPrice) : '');
      } else {
        setLocalMaxPrice(filters.maxPrice ? formatPriceForInput(filters.maxPrice) : '');
      }
    }
  };

  // Handle Enter key press to validate input
  const handlePriceInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    filterKey: 'minPrice' | 'maxPrice'
  ) => {
    if (e.key === 'Enter') {
      handlePriceInputBlur(e.currentTarget.value, filterKey);
    }
  };

  // Check if max < min for validation message
  const isPriceRangeInvalid = () => {
    if (!localMinPrice || !localMaxPrice) return false;

    const parsedMin = parseNumberFromInput(localMinPrice, numberFormat);
    const parsedMax = parseNumberFromInput(localMaxPrice, numberFormat);

    return !isNaN(parsedMin) && !isNaN(parsedMax) && parsedMax < parsedMin;
  };

  // Format price display for filter button text
  const formatPriceDisplayText = () => {
    if (!filters.minPrice && !filters.maxPrice) {
      return translations.options.anyPrice;
    }

    const minDisplay = filters.minPrice ? formatPriceForInput(filters.minPrice) : '0';
    const maxDisplay = filters.maxPrice
      ? formatPriceForInput(filters.maxPrice)
      : i18n.getFiltersPriceFormattingText('infinitySymbol');

    // Add currency symbol based on organization preferences
    const currencySymbol = orgSettings?.default_currency?.symbol || '$';

    if (numberFormat === 'comma') {
      return `${minDisplay} ${currencySymbol} - ${maxDisplay} ${currencySymbol}`;
    } else {
      return `${currencySymbol}${minDisplay} - ${currencySymbol}${maxDisplay}`;
    }
  };

  // Function to safely handle category filter changes

  // Function to safely handle status filter changes
  const handleStatusFilterChange = useCallback(
    (value: string) => {
      // Convert undefined and null values to 'all'
      const normalizedValue = value === undefined || value === null ? 'all' : value;

      // Debug the status filter change
      debugStatusFilter(
        'ProductsTableFilters',
        filters,
        `handleStatusFilterChange: ${value} -> ${normalizedValue}`
      );

      try {
        // Validate the status value
        if (!isValidStatusValue(normalizedValue)) {
          logError(new Error(`Invalid status value: ${normalizedValue}`), {
            component: 'ProductsTableFilters',
            action: 'handleStatusFilterChange',
            metadata: {
              value: value,
              normalizedValue: normalizedValue,
              context: 'invalid_status_value',
            },
          });
          onFilterChange('status', 'all');
          return;
        }

        // Use 'status' key to match FilterState interface in ProductsTable
        onFilterChange('status', normalizedValue);
      } catch (error) {
        logError(error instanceof Error ? error : new Error(String(error)), {
          component: 'ProductsTableFilters',
          action: 'handleStatusFilterChange',
          metadata: {
            value: value,
            normalizedValue: normalizedValue,
            context: 'status_filter_error',
          },
        });
        // In case of error, reset to 'all'
        onFilterChange('status', 'all');
      }
    },
    [filters, onFilterChange]
  );

  // Get the current status value for the UI using centralized utility
  const currentStatusValue = useMemo(() => {
    const value = getCurrentStatusValue(filters);
    debugStatusFilter('ProductsTableFilters', filters, `getCurrentStatusValue: ${value}`);
    return value || 'all'; // Ensure we always have a valid value
  }, [filters]);

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.sku) count++;
    if (filters.name) count++;
    // Handle family filter (array or string)
    if (
      Array.isArray(filters.family)
        ? filters.family.length > 0
        : filters.family && filters.family !== 'all'
    )
      count++;
    // Handle category filter (array or string)
    if (
      Array.isArray(filters.category)
        ? filters.category.length > 0
        : filters.category && filters.category !== 'all'
    )
      count++;
    // Handle brand filter (array or string)
    if (
      Array.isArray(filters.brand)
        ? filters.brand.length > 0
        : filters.brand && filters.brand !== 'all'
    )
      count++;
    if (filters.tags && Array.isArray(filters.tags) && filters.tags.length > 0) count++;
    if (filters.barcode) count++;
    if (filters.minPrice || filters.maxPrice) count++;
    // Check both status and is_active for status filter counting
    if ((filters.status && filters.status !== 'all') || filters.is_active !== undefined) count++;
    if (filters.created_at_from || filters.created_at_to) count++;
    if (filters.updated_at_from || filters.updated_at_to) count++;
    return count;
  }, [filters]);

  // Toggle collapse state
  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Render Grid View filters
  if (isGridView) {
    return (
      <Card className="enterprise-card-elevated border-border bg-card transition-all duration-300 ease-in-out">
        <CardContent
          className={cn('transition-all duration-300 ease-in-out', isCollapsed ? 'p-4' : 'p-6')}
        >
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <Filter className="size-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">{translations.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {getFiltersAppliedText(activeFiltersCount)}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* Reset Filters Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={onClearFilters}
                disabled={activeFiltersCount === 0}
                className="enterprise-button-secondary"
              >
                <RotateCcw className="size-4" />
                <span>{translations.buttons.resetAll}</span>
                {activeFiltersCount > 0 && (
                  <Badge variant="secondary" className="ml-1 text-xs">
                    {activeFiltersCount}
                  </Badge>
                )}
              </Button>

              {/* Collapse/Expand Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={toggleCollapse}
                className="enterprise-button-secondary"
              >
                {isCollapsed ? (
                  <>
                    <ChevronDown className="size-4" />
                    <span>{translations.buttons.expand}</span>
                  </>
                ) : (
                  <>
                    <ChevronUp className="size-4" />
                    <span>{translations.buttons.collapse}</span>
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Collapsible Filter Grid */}
          <div
            className={cn(
              'transition-all duration-300 ease-in-out overflow-hidden',
              isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'
            )}
          >
            {/* Filter Grid */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {/* SKU Filter */}
              <div className="space-y-2">
                <Label className="flex items-center space-x-2 text-sm font-medium text-foreground">
                  <Hash className="size-4 text-muted-foreground" />
                  <span>{translations.labels.sku}</span>
                </Label>
                <Input
                  placeholder={translations.placeholders.enterSku}
                  value={filters.sku ?? ''}
                  onChange={e => onFilterChange('sku', e.target.value)}
                  className="h-10 border-input bg-background text-sm"
                />
              </div>

              {/* Name Filter */}
              <div className="space-y-2">
                <Label className="flex items-center space-x-2 text-sm font-medium text-foreground">
                  <Type className="size-4 text-muted-foreground" />
                  <span>{translations.labels.productName}</span>
                </Label>
                <Input
                  placeholder={translations.placeholders.enterProductName}
                  value={filters.name ?? ''}
                  onChange={e => onFilterChange('name', e.target.value)}
                  className="h-10 border-input bg-background text-sm"
                />
              </div>

              {/* Family Filter */}
              <div className="space-y-2">
                <Label className="flex items-center space-x-2 text-sm font-medium text-foreground">
                  <Layers3 className="size-4 text-muted-foreground" />
                  <span>{translations.labels.family}</span>
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-10 w-full justify-between border-input bg-background text-sm font-normal text-foreground hover:bg-accent hover:text-accent-foreground"
                    >
                      <span className="truncate">
                        {Array.isArray(filters.family) && filters.family.length > 0
                          ? i18n.getFiltersMessageText('familiesSelected', {
                              count: filters.family.length,
                              plural: filters.family.length === 1 ? '' : 's',
                            })
                          : typeof filters.family === 'string' &&
                              filters.family !== 'all' &&
                              filters.family
                            ? families.find(f => String(f.id) === String(filters.family))?.label ||
                              families.find(f => String(f.id) === String(filters.family))?.code ||
                              `Family ${filters.family}`
                            : translations.options.allFamilies}
                      </span>
                      <Search className="ml-2 size-4 shrink-0 text-muted-foreground" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 border-border bg-popover p-4" align="start">
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Search className="size-4 text-muted-foreground" />
                        <Input
                          placeholder={translations.placeholders.searchFamilies}
                          className="enterprise-input h-9"
                          value={familySearch}
                          onChange={e => setFamilySearch(e.target.value)}
                        />
                      </div>

                      <div className="max-h-64 space-y-2 overflow-y-auto">
                        {isFamiliesLoading ? (
                          <div className="py-4 text-center text-sm text-muted-foreground">
                            <Loader2 className="mx-auto mb-2 size-5 animate-spin" />
                            {translations.messages.loadingFamilies}
                          </div>
                        ) : (
                          <>
                            {/* Selected families */}
                            {Array.isArray(filters.family) && filters.family.length > 0 && (
                              <div className="space-y-2">
                                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                  {i18n.getFiltersMessageText('selectedFamilies')}
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {filters.family.map(familyId => {
                                    const familyLabel =
                                      families.find(f => String(f.id) === String(familyId))
                                        ?.label ||
                                      families.find(f => String(f.id) === String(familyId))?.code ||
                                      `Family ${familyId}`;
                                    return (
                                      <Badge
                                        key={familyId}
                                        variant="secondary"
                                        className="cursor-pointer bg-primary/10 text-xs text-primary hover:bg-primary/20"
                                        onClick={() => {
                                          const newFamilies = Array.isArray(filters.family)
                                            ? filters.family.filter((f: string) => f !== familyId)
                                            : [];
                                          onFilterChange(
                                            'family',
                                            newFamilies.length > 0 ? newFamilies : []
                                          );
                                        }}
                                      >
                                        {familyLabel}
                                        <X className="ml-1 size-3" />
                                      </Badge>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* All Families option */}
                            <div className="flex items-center space-x-3 rounded-lg p-2 hover:bg-accent/60">
                              <Checkbox
                                id="family-all"
                                checked={!(Array.isArray(filters.family) && filters.family.length)}
                                onCheckedChange={checked => {
                                  if (checked) {
                                    onFilterChange('family', []);
                                  }
                                }}
                              />
                              <Label
                                htmlFor="family-all"
                                className="flex-1 cursor-pointer text-sm font-medium text-foreground"
                              >
                                {translations.options.allFamilies || 'All families'}
                              </Label>
                            </div>

                            {/* Filter families based on search */}
                            {families
                              .filter(family => {
                                const searchTerm = familySearch.toLowerCase();
                                return (
                                  (family.label &&
                                    family.label.toLowerCase().includes(searchTerm)) ||
                                  (family.code && family.code.toLowerCase().includes(searchTerm))
                                );
                              })
                              .slice(0, 100)
                              .map(family => (
                                <div
                                  key={family.id}
                                  className="flex items-center space-x-3 rounded-lg p-2 hover:bg-accent/60"
                                >
                                  <Checkbox
                                    id={`family-${family.id}`}
                                    checked={
                                      Array.isArray(filters.family)
                                        ? filters.family.includes(String(family.id))
                                        : String(filters.family) === String(family.id)
                                    }
                                    onCheckedChange={checked => {
                                      // Create new families array for multi-select
                                      let newFamilies: string[];

                                      if (Array.isArray(filters.family)) {
                                        newFamilies = checked
                                          ? Array.from(
                                              new Set([...filters.family, String(family.id)])
                                            )
                                          : filters.family.filter(
                                              (f: string) => f !== String(family.id)
                                            );
                                      } else {
                                        newFamilies = checked ? [String(family.id)] : [];
                                      }

                                      onFilterChange('family', newFamilies);
                                    }}
                                  />
                                  <Label
                                    htmlFor={`family-${family.id}`}
                                    className="flex-1 cursor-pointer truncate text-sm text-foreground"
                                  >
                                    {family.label || family.code || `Family ${family.id}`}
                                  </Label>
                                </div>
                              ))}

                            {families.filter(family => {
                              const searchTerm = familySearch.toLowerCase();
                              return (
                                (family.label && family.label.toLowerCase().includes(searchTerm)) ||
                                (family.code && family.code.toLowerCase().includes(searchTerm))
                              );
                            }).length === 0 &&
                              familySearch && (
                                <div className="py-2 text-center text-sm text-muted-foreground">
                                  {getNoFamiliesFoundText(familySearch)}
                                </div>
                              )}

                            {families.filter(family => {
                              const searchTerm = familySearch.toLowerCase();
                              return (
                                (family.label && family.label.toLowerCase().includes(searchTerm)) ||
                                (family.code && family.code.toLowerCase().includes(searchTerm))
                              );
                            }).length > 100 && (
                              <div className="border-t border-border py-2 text-center text-xs text-muted-foreground">
                                {i18n.getFiltersMessageText('showingFirstResults')}
                              </div>
                            )}

                            {families.length === 0 && !isFamiliesLoading && (
                              <div className="py-2 text-center text-sm text-muted-foreground">
                                {translations.messages.noFamiliesAvailable}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Category Filter */}
              <div className="space-y-2">
                <Label className="flex items-center space-x-2 text-sm font-medium text-foreground">
                  <Package className="size-4 text-muted-foreground" />
                  <span>{translations.labels.category}</span>
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-10 w-full justify-between border-input bg-background text-sm font-normal text-foreground hover:bg-accent hover:text-accent-foreground"
                    >
                      <span className="truncate">
                        {Array.isArray(filters.category) && filters.category.length > 0
                          ? i18n.getFiltersMessageText('categoriesSelected', {
                              count: filters.category.length,
                              plural: filters.category.length === 1 ? '' : 's',
                            })
                          : typeof filters.category === 'string' &&
                              filters.category !== 'all' &&
                              filters.category
                            ? uniqueCategories.find(
                                cat => String(cat.value) === String(filters.category)
                              )?.label || `${translations.labels.category} ${filters.category}`
                            : translations.options.allCategories}
                      </span>
                      <Search className="ml-2 size-4 shrink-0 text-muted-foreground" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 border-border bg-popover p-4" align="start">
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Search className="size-4 text-muted-foreground" />
                        <Input
                          placeholder={translations.placeholders.searchCategories}
                          className="enterprise-input h-9"
                          value={categorySearch}
                          onChange={e => setCategorySearch(e.target.value)}
                        />
                      </div>

                      {/* Selected categories */}
                      {Array.isArray(filters.category) && filters.category.length > 0 && (
                        <div className="space-y-2">
                          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {i18n.getFiltersMessageText('selectedCategories')}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {filters.category.map(categoryId => {
                              const categoryLabel =
                                categoryId === 'uncategorized'
                                  ? i18n.getColumnPlaceholderText('uncategorized')
                                  : uniqueCategories.find(
                                      cat => String(cat.value) === String(categoryId)
                                    )?.label || categoryId;
                              return (
                                <Badge
                                  key={categoryId}
                                  variant="secondary"
                                  className="cursor-pointer bg-primary/10 text-xs text-primary hover:bg-primary/20"
                                  onClick={() => {
                                    const newCategories = Array.isArray(filters.category)
                                      ? filters.category.filter((c: string) => c !== categoryId)
                                      : [];
                                    onFilterChange(
                                      'category',
                                      newCategories.length > 0 ? newCategories : []
                                    );
                                  }}
                                >
                                  {categoryLabel}
                                  <X className="ml-1 size-3" />
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div className="max-h-64 space-y-2 overflow-y-auto">
                        {/* Uncategorized option */}
                        <div className="flex items-center space-x-3 rounded-lg p-2 hover:bg-accent/60">
                          <Checkbox
                            id="category-uncategorized"
                            checked={
                              Array.isArray(filters.category)
                                ? filters.category.includes('uncategorized')
                                : String(filters.category) === 'uncategorized'
                            }
                            onCheckedChange={checked => {
                              // Create new categories array for multi-select
                              let newCategories: string[];

                              if (Array.isArray(filters.category)) {
                                newCategories = checked
                                  ? Array.from(new Set([...filters.category, 'uncategorized']))
                                  : filters.category.filter((c: string) => c !== 'uncategorized');
                              } else {
                                newCategories = checked ? ['uncategorized'] : [];
                              }

                              onFilterChange('category', newCategories);
                            }}
                          />
                          <Label
                            htmlFor="category-uncategorized"
                            className="flex-1 cursor-pointer text-sm text-foreground"
                          >
                            {i18n.getColumnPlaceholderText('uncategorized')}
                          </Label>
                        </div>

                        {/* Filter categories based on search */}
                        {Array.isArray(uniqueCategories) && uniqueCategories.length > 0 ? (
                          uniqueCategories
                            .filter(
                              cat =>
                                cat &&
                                cat.label.toLowerCase().includes(categorySearch.toLowerCase())
                            )
                            .slice(0, 100)
                            .map((cat, index) => (
                              <div
                                key={`cat-${index}-${cat.value}`}
                                className="flex items-center space-x-3 rounded-lg p-2 hover:bg-accent/60"
                              >
                                <Checkbox
                                  id={`category-${cat.value}`}
                                  checked={
                                    Array.isArray(filters.category)
                                      ? filters.category.includes(String(cat.value))
                                      : String(filters.category) === String(cat.value)
                                  }
                                  onCheckedChange={checked => {
                                    // Create new categories array for multi-select
                                    let newCategories: string[];

                                    if (Array.isArray(filters.category)) {
                                      newCategories = checked
                                        ? Array.from(
                                            new Set([...filters.category, String(cat.value)])
                                          )
                                        : filters.category.filter(
                                            (c: string) => c !== String(cat.value)
                                          );
                                    } else {
                                      newCategories = checked ? [String(cat.value)] : [];
                                    }

                                    onFilterChange('category', newCategories);
                                  }}
                                />
                                <Label
                                  htmlFor={`category-${cat.value}`}
                                  className="flex-1 cursor-pointer truncate text-sm text-foreground"
                                >
                                  {cat.label}
                                </Label>
                              </div>
                            ))
                        ) : (
                          <div className="py-4 text-center text-sm text-muted-foreground">
                            {translations.messages.noCategoriesAvailable}
                          </div>
                        )}

                        {uniqueCategories.filter(
                          cat =>
                            cat && cat.label.toLowerCase().includes(categorySearch.toLowerCase())
                        ).length === 0 &&
                          categorySearch && (
                            <div className="py-4 text-center text-sm text-muted-foreground">
                              {getNoCategoriesFoundText(categorySearch)}
                            </div>
                          )}

                        {uniqueCategories.filter(
                          cat =>
                            cat && cat.label.toLowerCase().includes(categorySearch.toLowerCase())
                        ).length > 100 && (
                          <div className="border-t border-border py-2 text-center text-xs text-muted-foreground">
                            {i18n.getFiltersMessageText('showingFirstResults')}
                          </div>
                        )}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Brand Filter */}
              <div className="space-y-2">
                <Label className="flex items-center space-x-2 text-sm font-medium text-foreground">
                  <Building2 className="size-4 text-muted-foreground" />
                  <span>{translations.labels.brand}</span>
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-10 w-full justify-between border-input bg-background text-sm font-normal text-foreground hover:bg-accent hover:text-accent-foreground"
                    >
                      <span className="truncate">
                        {Array.isArray(filters.brand) && filters.brand.length > 0
                          ? i18n.getFiltersMessageText('brandsSelected', {
                              count: filters.brand.length,
                              plural: filters.brand.length === 1 ? '' : 's',
                            })
                          : typeof filters.brand === 'string' &&
                              filters.brand !== 'all' &&
                              filters.brand
                            ? filters.brand
                            : translations.options.allBrands}
                      </span>
                      <Search className="ml-2 size-4 shrink-0 text-muted-foreground" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 border-border bg-popover p-4" align="start">
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Search className="size-4 text-muted-foreground" />
                        <Input
                          placeholder={translations.placeholders.searchBrands}
                          className="enterprise-input h-9"
                          value={brandSearch}
                          onChange={e => setBrandSearch(e.target.value)}
                        />
                      </div>

                      {/* Selected brands */}
                      {Array.isArray(filters.brand) && filters.brand.length > 0 && (
                        <div className="space-y-2">
                          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {i18n.getFiltersMessageText('selectedBrands')}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {filters.brand.map(brand => (
                              <Badge
                                key={brand}
                                variant="secondary"
                                className="cursor-pointer bg-primary/10 text-xs text-primary hover:bg-primary/20"
                                onClick={() => {
                                  const newBrands = Array.isArray(filters.brand)
                                    ? filters.brand.filter((b: string) => b !== brand)
                                    : [];
                                  onFilterChange('brand', newBrands.length > 0 ? newBrands : []);
                                }}
                              >
                                {brand}
                                <X className="ml-1 size-3" />
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="max-h-64 space-y-2 overflow-y-auto">
                        {/* All Brands option */}
                        <div className="flex items-center space-x-3 rounded-lg p-2 hover:bg-accent/60">
                          <Checkbox
                            id="brand-all"
                            checked={!(Array.isArray(filters.brand) && filters.brand.length)}
                            onCheckedChange={checked => {
                              if (checked) {
                                onFilterChange('brand', []); // reset to default – empty array
                              }
                            }}
                          />
                          <Label
                            htmlFor="brand-all"
                            className="flex-1 cursor-pointer text-sm font-medium text-foreground"
                          >
                            {translations.options.allBrands || 'All brands'}
                          </Label>
                        </div>

                        {/* Filter brands based on search */}
                        {uniqueBrands.length > 0 ? (
                          uniqueBrands
                            .filter(
                              brand =>
                                brand && brand.toLowerCase().includes(brandSearch.toLowerCase())
                            )
                            .slice(0, 100)
                            .map((brand, idx) => (
                              <div
                                key={brand || idx}
                                className="flex items-center space-x-3 rounded-lg p-2 hover:bg-accent/60"
                              >
                                <Checkbox
                                  id={`brand-${brand}`}
                                  checked={
                                    Array.isArray(filters.brand)
                                      ? filters.brand.includes(brand)
                                      : String(filters.brand) === String(brand)
                                  }
                                  onCheckedChange={checked => {
                                    // Create new brands array for multi-select
                                    let newBrands: string[];

                                    if (Array.isArray(filters.brand)) {
                                      newBrands = checked
                                        ? Array.from(new Set([...filters.brand, brand]))
                                        : filters.brand.filter((b: string) => b !== brand);
                                    } else {
                                      newBrands = checked ? [brand] : [];
                                    }

                                    onFilterChange('brand', newBrands);
                                  }}
                                />
                                <Label
                                  htmlFor={`brand-${brand}`}
                                  className="flex-1 cursor-pointer truncate text-sm text-foreground"
                                >
                                  {brand}
                                </Label>
                              </div>
                            ))
                        ) : (
                          <div className="py-4 text-center text-sm text-muted-foreground">
                            {translations.messages.noBrandsAvailable}
                          </div>
                        )}

                        {uniqueBrands.filter(
                          brand => brand && brand.toLowerCase().includes(brandSearch.toLowerCase())
                        ).length === 0 &&
                          brandSearch && (
                            <div className="py-4 text-center text-sm text-muted-foreground">
                              {getNoBrandsFoundText(brandSearch)}
                            </div>
                          )}

                        {uniqueBrands.filter(
                          brand => brand && brand.toLowerCase().includes(brandSearch.toLowerCase())
                        ).length > 100 && (
                          <div className="border-t border-border py-2 text-center text-xs text-muted-foreground">
                            {i18n.getFiltersMessageText('showingFirstResults')}
                          </div>
                        )}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Tags Filter */}
              <div className="space-y-2">
                <Label className="flex items-center space-x-2 text-sm font-medium text-foreground">
                  <span className="truncate">
                    {Array.isArray(filters.tags) && filters.tags.length > 0
                      ? i18n.getFiltersMessageText('tagsSelected', {
                          count: filters.tags.length,
                          plural: filters.tags.length === 1 ? '' : 's',
                        })
                      : i18n.getFiltersMessageText('noTagsSelected')}
                  </span>
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-10 w-full justify-between border-input bg-background text-sm font-normal text-foreground hover:bg-accent hover:text-accent-foreground"
                    >
                      <span className="truncate">
                        {Array.isArray(filters.tags) && filters.tags.length > 0
                          ? i18n.getFiltersMessageText('tagsSelected', {
                              count: filters.tags.length,
                              plural: filters.tags.length === 1 ? '' : 's',
                            })
                          : i18n.getFiltersMessageText('noTagsSelected')}
                      </span>
                      <Search className="ml-2 size-4 shrink-0 text-muted-foreground" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 border-border bg-popover p-4" align="start">
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Search className="size-4 text-muted-foreground" />
                        <Input
                          placeholder={translations.placeholders.searchTags}
                          className="enterprise-input h-9"
                          value={tagSearch}
                          onChange={e => setTagSearch(e.target.value)}
                        />
                      </div>

                      {/* Selected tags */}
                      {Array.isArray(filters.tags) && filters.tags.length > 0 && (
                        <div className="space-y-2">
                          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {i18n.getFiltersMessageText('selectedTags')}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {filters.tags.map(tag => (
                              <Badge
                                key={tag}
                                variant="secondary"
                                className="cursor-pointer bg-primary/10 text-xs text-primary hover:bg-primary/20"
                                onClick={() => {
                                  const newTags = filters.tags.filter((t: string) => t !== tag);
                                  onFilterChange('tags', newTags);
                                }}
                              >
                                {tag}
                                <X className="ml-1 size-3" />
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="max-h-64 space-y-2 overflow-y-auto">
                        {Array.isArray(uniqueTags) && uniqueTags.length > 0 ? (
                          uniqueTags
                            .filter(
                              tag => tag && tag.toLowerCase().includes(tagSearch.toLowerCase())
                            )
                            .slice(0, 100)
                            .map(tag => (
                              <div
                                key={tag}
                                className="flex items-center space-x-3 rounded-lg p-2 hover:bg-accent/60"
                              >
                                <Checkbox
                                  id={`tag-${tag}`}
                                  checked={
                                    Array.isArray(filters.tags) && filters.tags.includes(tag)
                                  }
                                  onCheckedChange={checked => {
                                    // Create new tags array
                                    const newTags = checked
                                      ? [...(Array.isArray(filters.tags) ? filters.tags : []), tag]
                                      : Array.isArray(filters.tags)
                                        ? filters.tags.filter((t: string) => t !== tag)
                                        : [];

                                    onFilterChange('tags', newTags);
                                  }}
                                />
                                <Label
                                  htmlFor={`tag-${tag}`}
                                  className="flex-1 cursor-pointer truncate text-sm text-foreground"
                                >
                                  {tag}
                                </Label>
                              </div>
                            ))
                        ) : (
                          <div className="py-4 text-center text-sm text-muted-foreground">
                            {translations.messages.noTagsAvailable}
                          </div>
                        )}

                        {uniqueTags.filter(
                          tag => tag && tag.toLowerCase().includes(tagSearch.toLowerCase())
                        ).length === 0 &&
                          tagSearch && (
                            <div className="py-4 text-center text-sm text-muted-foreground">
                              {getNoTagsFoundText(tagSearch)}
                            </div>
                          )}

                        {uniqueTags.filter(
                          tag => tag && tag.toLowerCase().includes(tagSearch.toLowerCase())
                        ).length > 100 && (
                          <div className="border-t border-border py-2 text-center text-xs text-muted-foreground">
                            {i18n.getFiltersMessageText('showingFirstResults')}
                          </div>
                        )}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Barcode Filter */}
              <div className="space-y-2">
                <Label className="flex items-center space-x-2 text-sm font-medium text-foreground">
                  <Hash className="size-4 text-muted-foreground" />
                  <span>{translations.labels.barcode}</span>
                </Label>
                <Input
                  placeholder={translations.placeholders.enterBarcode}
                  value={filters.barcode ?? ''}
                  onChange={e => onFilterChange('barcode', e.target.value)}
                  className="h-10 border-input bg-background text-xs font-normal"
                  onClick={e => e.stopPropagation()}
                />
              </div>

              {/* Price Range Filter */}
              <div className="space-y-2">
                <Label className="flex items-center space-x-2 text-sm font-medium text-foreground">
                  <DollarSign className="size-4 text-muted-foreground" />
                  <span>{translations.labels.price}</span>
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'h-10 text-sm w-full justify-between font-normal border-input bg-background',
                        (filters.minPrice || filters.maxPrice) &&
                          'border-primary bg-primary/5 text-primary'
                      )}
                    >
                      <span className="truncate">{formatPriceDisplayText()}</span>
                      <DollarSign className="ml-2 size-4 shrink-0 text-muted-foreground" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 border-border bg-popover p-4" align="start">
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label
                            htmlFor="price-min"
                            className="text-sm font-medium text-foreground"
                          >
                            {i18n.getFiltersDateLabelText('minimum')}
                          </Label>
                          <Input
                            id="price-min"
                            type="text"
                            placeholder={getPricePlaceholder()}
                            className="enterprise-input h-9"
                            value={localMinPrice}
                            onChange={e => setLocalMinPrice(e.target.value)}
                            onBlur={e => handlePriceInputBlur(e.target.value, 'minPrice')}
                            onKeyDown={e => handlePriceInputKeyDown(e, 'minPrice')}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label
                            htmlFor="price-max"
                            className="text-sm font-medium text-foreground"
                          >
                            {i18n.getFiltersDateLabelText('maximum')}
                          </Label>
                          <Input
                            id="price-max"
                            type="text"
                            placeholder={getPricePlaceholder()}
                            className="enterprise-input h-9"
                            value={localMaxPrice}
                            onChange={e => setLocalMaxPrice(e.target.value)}
                            onBlur={e => handlePriceInputBlur(e.target.value, 'maxPrice')}
                            onKeyDown={e => handlePriceInputKeyDown(e, 'maxPrice')}
                          />
                        </div>
                      </div>

                      {/* Show validation message if max < min */}
                      {isPriceRangeInvalid() && (
                        <div className="rounded-lg bg-red-50 p-2 text-sm text-red-600">
                          {translations.messages.priceRangeInvalid}
                        </div>
                      )}

                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          onFilterChange('minPrice', undefined);
                          onFilterChange('maxPrice', undefined);
                        }}
                      >
                        {i18n.getFiltersButtonText('clearPriceFilter')}
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Status Filter */}
              <div className="space-y-2">
                <Label className="flex items-center space-x-2 text-sm font-medium text-foreground">
                  <Package className="size-4 text-muted-foreground" />
                  <span>{translations.labels.status}</span>
                </Label>
                <Select
                  value={currentStatusValue || 'all'}
                  onValueChange={handleStatusFilterChange}
                >
                  <SelectTrigger className="h-10 border-input bg-background text-sm">
                    <SelectValue placeholder={translations.options.allStatuses} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{translations.options.allStatuses}</SelectItem>
                    <SelectItem value="true">{translations.options.active}</SelectItem>
                    <SelectItem value="false">{translations.options.inactive}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Created Date Filter */}
              <div className="space-y-2">
                <Label className="flex items-center space-x-2 text-sm font-medium text-foreground">
                  <Calendar className="size-4 text-muted-foreground" />
                  <span>{translations.labels.createdAt}</span>
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'h-10 text-sm w-full justify-between font-normal border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
                        (filters.created_at_from || filters.created_at_to) &&
                          'border-primary bg-primary/5 text-primary'
                      )}
                    >
                      <span className="truncate">
                        {filters.created_at_from || filters.created_at_to
                          ? `${formatDateForDisplay(filters.created_at_from) || i18n.getFiltersDateLabelText('start')} - ${formatDateForDisplay(filters.created_at_to) || i18n.getFiltersDateLabelText('end')}`
                          : i18n.getFiltersDateLabelText('selectDateRange')}
                      </span>
                      <Calendar className="ml-2 size-4 shrink-0 text-muted-foreground" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 border-border bg-popover p-4" align="start">
                    <div className="space-y-4">
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label
                            htmlFor="created_at-from"
                            className="text-sm font-medium text-foreground"
                          >
                            {i18n.getFiltersDateLabelText('fromDate')}
                          </Label>
                          <Input
                            id="created_at-from"
                            type="date"
                            className="enterprise-input h-9"
                            value={filters.created_at_from || ''}
                            onChange={e => {
                              const value = e.target.value || undefined;
                              onFilterChange('created_at_from', value);
                            }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label
                            htmlFor="created_at-to"
                            className="text-sm font-medium text-foreground"
                          >
                            {i18n.getFiltersDateLabelText('toDate')}
                          </Label>
                          <Input
                            id="created_at-to"
                            type="date"
                            className="enterprise-input h-9"
                            value={filters.created_at_to || ''}
                            onChange={e => {
                              const value = e.target.value || undefined;
                              onFilterChange('created_at_to', value);
                            }}
                          />
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          onFilterChange('created_at_from', '');
                          onFilterChange('created_at_to', '');
                        }}
                      >
                        {i18n.getFiltersButtonText('reset')}
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Updated Date Filter */}
              <div className="space-y-2">
                <Label className="flex items-center space-x-2 text-sm font-medium text-foreground">
                  <Calendar className="size-4 text-muted-foreground" />
                  <span>{translations.labels.updatedAt}</span>
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'h-10 text-sm w-full justify-between font-normal border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
                        (filters.updated_at_from || filters.updated_at_to) &&
                          'border-primary bg-primary/5 text-primary'
                      )}
                    >
                      <span className="truncate">
                        {filters.updated_at_from || filters.updated_at_to
                          ? `${formatDateForDisplay(filters.updated_at_from) || i18n.getFiltersDateLabelText('start')} - ${formatDateForDisplay(filters.updated_at_to) || i18n.getFiltersDateLabelText('end')}`
                          : i18n.getFiltersDateLabelText('selectDateRange')}
                      </span>
                      <Calendar className="ml-2 size-4 shrink-0 text-muted-foreground" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 border-border bg-popover p-4" align="start">
                    <div className="space-y-4">
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label
                            htmlFor="updated_at-from"
                            className="text-sm font-medium text-foreground"
                          >
                            {i18n.getFiltersDateLabelText('fromDate')}
                          </Label>
                          <Input
                            id="updated_at-from"
                            type="date"
                            className="enterprise-input h-9"
                            value={filters.updated_at_from || ''}
                            onChange={e => {
                              const value = e.target.value || undefined;
                              onFilterChange('updated_at_from', value);
                            }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label
                            htmlFor="updated_at-to"
                            className="text-sm font-medium text-foreground"
                          >
                            {i18n.getFiltersDateLabelText('toDate')}
                          </Label>
                          <Input
                            id="updated_at-to"
                            type="date"
                            className="enterprise-input h-9"
                            value={filters.updated_at_to || ''}
                            onChange={e => {
                              const value = e.target.value || undefined;
                              onFilterChange('updated_at_to', value);
                            }}
                          />
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          onFilterChange('updated_at_from', '');
                          onFilterChange('updated_at_to', '');
                        }}
                      >
                        {i18n.getFiltersButtonText('reset')}
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Original Table Row Layout for List View (unchanged)
  return (
    <>
      {/* Single row of filters */}
      <TableRow className="sticky top-9 z-50 h-6 border-b border-border bg-muted/30">
        {/* Reset filters button in first column (select checkbox column) */}
        <TableHead key="filter-select" className="p-1">
          <Button
            variant="outline"
            size="sm"
            onClick={onClearFilters}
            className="h-7 rounded-full border-input bg-background px-3 text-xs text-foreground 
                      shadow-sm transition-colors hover:bg-accent"
          >
            {translations.buttons.resetAll}
          </Button>
        </TableHead>

        {/* Expander column - empty cell */}
        <TableHead key="filter-expander" className="w-9 p-1" />

        {/* Thumbnail column - empty cell */}
        <TableHead key="filter-thumbnail" className="p-1" />

        {/* SKU filter */}
        <TableHead key="filter-sku" className="p-2">
          <Input
            placeholder={translations.placeholders.enterSku}
            value={filters.sku ?? ''}
            onChange={e => onFilterChange('sku', e.target.value)}
            className="h-7 border-input bg-background text-xs font-normal"
            onClick={e => e.stopPropagation()}
          />
        </TableHead>

        {/* Name filter */}
        <TableHead key="filter-name" className="p-2">
          <Input
            placeholder={translations.placeholders.enterProductName}
            value={filters.name ?? ''}
            onChange={e => onFilterChange('name', e.target.value)}
            className="h-7 border-input bg-background text-xs font-normal"
            onClick={e => e.stopPropagation()}
          />
        </TableHead>

        {/* Family filter */}
        <TableHead key="filter-family" className="p-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="h-7 w-full justify-start border-input bg-background text-xs font-normal"
                onClick={e => e.stopPropagation()}
              >
                <span>
                  {Array.isArray(filters.family) && filters.family.length > 0
                    ? i18n.getFiltersMessageText('familiesSelected', {
                        count: filters.family.length,
                        plural: filters.family.length === 1 ? '' : 's',
                      })
                    : typeof filters.family === 'string' &&
                        filters.family !== 'all' &&
                        filters.family
                      ? families.find(f => String(f.id) === String(filters.family))?.label ||
                        families.find(f => String(f.id) === String(filters.family))?.code ||
                        `Family ${filters.family}`
                      : translations.options.allFamilies}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3" align="start">
              <div className="space-y-2">
                {/* Search input for families */}
                <Input
                  placeholder={translations.placeholders.searchFamilies}
                  className="h-8 text-xs"
                  value={familySearch}
                  onChange={e => setFamilySearch(e.target.value)}
                />

                <div className="max-h-48 overflow-y-auto pr-2">
                  {isFamiliesLoading ? (
                    <div className="py-4 text-center text-sm text-muted-foreground">
                      <Loader2 className="mx-auto mb-2 size-5 animate-spin" />
                      {translations.messages.loadingFamilies}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {/* All Families option */}
                      <div className="flex items-center space-x-3 rounded-lg p-2 hover:bg-accent/60">
                        <Checkbox
                          id="family-all"
                          checked={!(Array.isArray(filters.family) && filters.family.length)}
                          onCheckedChange={checked => {
                            if (checked) {
                              onFilterChange('family', []);
                            }
                          }}
                        />
                        <Label htmlFor="family-all" className="ml-2 cursor-pointer text-sm">
                          {translations.options.allFamilies}
                        </Label>
                      </div>

                      {/* Filter families based on search */}
                      {families
                        .filter(family => {
                          const searchTerm = familySearch.toLowerCase();
                          return (
                            (family.label && family.label.toLowerCase().includes(searchTerm)) ||
                            (family.code && family.code.toLowerCase().includes(searchTerm))
                          );
                        })
                        .slice(0, 100)
                        .map(family => (
                          <div
                            key={family.id}
                            className="flex items-center space-x-3 rounded-lg p-2 hover:bg-accent/60"
                          >
                            <Checkbox
                              id={`family-${family.id}`}
                              checked={
                                Array.isArray(filters.family)
                                  ? filters.family.includes(String(family.id))
                                  : String(filters.family) === String(family.id)
                              }
                              onCheckedChange={checked => {
                                // Create new families array for multi-select
                                let newFamilies: string[];

                                if (Array.isArray(filters.family)) {
                                  newFamilies = checked
                                    ? Array.from(new Set([...filters.family, String(family.id)]))
                                    : filters.family.filter((f: string) => f !== String(family.id));
                                } else {
                                  newFamilies = checked ? [String(family.id)] : [];
                                }

                                onFilterChange('family', newFamilies);
                              }}
                            />
                            <Label
                              htmlFor={`family-${family.id}`}
                              className="flex-1 cursor-pointer truncate text-sm"
                            >
                              {family.label || family.code || `Family ${family.id}`}
                            </Label>
                          </div>
                        ))}

                      {families.filter(family => {
                        const searchTerm = familySearch.toLowerCase();
                        return (
                          (family.label && family.label.toLowerCase().includes(searchTerm)) ||
                          (family.code && family.code.toLowerCase().includes(searchTerm))
                        );
                      }).length === 0 &&
                        familySearch && (
                          <div className="py-2 text-center text-xs text-muted-foreground">
                            {getNoFamiliesFoundText(familySearch)}
                          </div>
                        )}

                      {families.length === 0 && !isFamiliesLoading && (
                        <div className="py-2 text-center text-xs text-muted-foreground">
                          {translations.messages.noFamiliesAvailable}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </TableHead>

        {/* Category filter - Updated to handle nested categories */}
        <TableHead key="filter-category" className="p-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="h-7 w-full justify-start border-input bg-background text-xs font-normal"
                onClick={e => e.stopPropagation()}
              >
                <span>
                  {Array.isArray(filters.category) && filters.category.length > 0
                    ? i18n.getFiltersMessageText('categoriesSelected', {
                        count: filters.category.length,
                        plural: filters.category.length === 1 ? '' : 's',
                      })
                    : typeof filters.category === 'string' &&
                        filters.category !== 'all' &&
                        filters.category
                      ? uniqueCategories.find(cat => String(cat.value) === String(filters.category))
                          ?.label || `Category ${filters.category}`
                      : translations.options.allCategories}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3" align="start">
              <div className="space-y-2">
                {/* Search input for categories */}
                <Input
                  placeholder={translations.placeholders.searchCategories}
                  className="h-8 text-xs"
                  value={categorySearch}
                  onChange={e => setCategorySearch(e.target.value)}
                />

                <div className="max-h-48 overflow-y-auto pr-2">
                  <div className="space-y-1">
                    {/* All Categories option */}
                    <div className="flex items-center space-x-3 rounded-lg p-2 hover:bg-accent/60">
                      <Checkbox
                        id="category-all"
                        checked={!(Array.isArray(filters.category) && filters.category.length)}
                        onCheckedChange={checked => {
                          if (checked) {
                            onFilterChange('category', []);
                          }
                        }}
                      />
                      <Label
                        htmlFor="category-all"
                        className="flex-1 cursor-pointer text-sm font-medium text-foreground"
                      >
                        {translations.options.allCategories || 'All categories'}
                      </Label>
                    </div>

                    {/* Uncategorized option */}
                    <div className="flex items-center space-x-3 rounded-lg p-2 hover:bg-accent/60">
                      <Checkbox
                        id="category-uncategorized"
                        checked={
                          Array.isArray(filters.category)
                            ? filters.category.includes('uncategorized')
                            : String(filters.category) === 'uncategorized'
                        }
                        onCheckedChange={checked => {
                          // Create new categories array for multi-select
                          let newCategories: string[];

                          if (Array.isArray(filters.category)) {
                            newCategories = checked
                              ? Array.from(new Set([...filters.category, 'uncategorized']))
                              : filters.category.filter((c: string) => c !== 'uncategorized');
                          } else {
                            newCategories = checked ? ['uncategorized'] : [];
                          }

                          onFilterChange('category', newCategories);
                        }}
                      />
                      <Label
                        htmlFor="category-uncategorized"
                        className="flex-1 cursor-pointer text-sm text-foreground"
                      >
                        {i18n.getColumnPlaceholderText('uncategorized')}
                      </Label>
                    </div>

                    {/* Filter categories based on search */}
                    {Array.isArray(uniqueCategories) && uniqueCategories.length > 0 ? (
                      uniqueCategories
                        .filter(
                          cat =>
                            cat && cat.label.toLowerCase().includes(categorySearch.toLowerCase())
                        )
                        .slice(0, 100)
                        .map((cat, index) => (
                          <div
                            key={`cat-${index}-${cat.value}`}
                            className="flex items-center space-x-3 rounded-lg p-2 hover:bg-accent/60"
                          >
                            <Checkbox
                              id={`category-${cat.value}`}
                              checked={
                                Array.isArray(filters.category)
                                  ? filters.category.includes(String(cat.value))
                                  : String(filters.category) === String(cat.value)
                              }
                              onCheckedChange={checked => {
                                // Create new categories array for multi-select
                                let newCategories: string[];

                                if (Array.isArray(filters.category)) {
                                  newCategories = checked
                                    ? Array.from(new Set([...filters.category, String(cat.value)]))
                                    : filters.category.filter(
                                        (c: string) => c !== String(cat.value)
                                      );
                                } else {
                                  newCategories = checked ? [String(cat.value)] : [];
                                }

                                onFilterChange('category', newCategories);
                              }}
                            />
                            <Label
                              htmlFor={`category-${cat.value}`}
                              className="flex-1 cursor-pointer truncate text-sm text-foreground"
                            >
                              {cat.label}
                            </Label>
                          </div>
                        ))
                    ) : (
                      <div className="py-4 text-center text-sm text-muted-foreground">
                        {translations.messages.noCategoriesAvailable}
                      </div>
                    )}

                    {uniqueCategories.filter(
                      cat => cat && cat.label.toLowerCase().includes(categorySearch.toLowerCase())
                    ).length === 0 &&
                      categorySearch && (
                        <div className="py-4 text-center text-sm text-muted-foreground">
                          {getNoCategoriesFoundText(categorySearch)}
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </TableHead>

        {/* Brand filter */}
        <TableHead key="filter-brand" className="hidden p-2 md:table-cell">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="h-7 w-full justify-start border-input bg-background text-xs font-normal"
                onClick={e => e.stopPropagation()}
              >
                <span>
                  {Array.isArray(filters.brand) && filters.brand.length > 0
                    ? i18n.getFiltersMessageText('brandsSelected', {
                        count: filters.brand.length,
                        plural: filters.brand.length === 1 ? '' : 's',
                      })
                    : typeof filters.brand === 'string' && filters.brand !== 'all' && filters.brand
                      ? filters.brand
                      : translations.options.allBrands}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3" align="start">
              <div className="space-y-2">
                {/* Search input for brands */}
                <Input
                  placeholder={translations.placeholders.searchBrands}
                  className="h-8 text-xs"
                  value={brandSearch}
                  onChange={e => setBrandSearch(e.target.value)}
                />

                <div className="max-h-48 overflow-y-auto pr-2">
                  <div className="space-y-1">
                    {/* All Brands option */}
                    <div className="flex items-center space-x-3 rounded-lg p-2 hover:bg-accent/60">
                      <Checkbox
                        id="brand-all"
                        checked={!(Array.isArray(filters.brand) && filters.brand.length)}
                        onCheckedChange={checked => {
                          if (checked) {
                            onFilterChange('brand', []);
                          }
                        }}
                      />
                      <Label htmlFor="brand-all" className="ml-2 cursor-pointer text-sm">
                        {translations.options.allBrands}
                      </Label>
                    </div>

                    {/* Filter brands based on search */}
                    {uniqueBrands
                      .filter(
                        brand => brand && brand.toLowerCase().includes(brandSearch.toLowerCase())
                      )
                      .slice(0, 100)
                      .map((brand, idx) => (
                        <div
                          key={brand || idx}
                          className="flex items-center space-x-3 rounded-lg p-2 hover:bg-accent/60"
                        >
                          <Checkbox
                            id={`brand-${brand}`}
                            checked={
                              Array.isArray(filters.brand)
                                ? filters.brand.includes(brand)
                                : String(filters.brand) === String(brand)
                            }
                            onCheckedChange={checked => {
                              // Create new brands array for multi-select
                              let newBrands: string[];

                              if (Array.isArray(filters.brand)) {
                                newBrands = checked
                                  ? Array.from(new Set([...filters.brand, brand]))
                                  : filters.brand.filter((b: string) => b !== brand);
                              } else {
                                newBrands = checked ? [brand] : [];
                              }

                              onFilterChange('brand', newBrands);
                            }}
                          />
                          <Label
                            htmlFor={`brand-${brand}`}
                            className="flex-1 cursor-pointer truncate text-sm"
                          >
                            {brand}
                          </Label>
                        </div>
                      ))}

                    {uniqueBrands.filter(
                      brand => brand && brand.toLowerCase().includes(brandSearch.toLowerCase())
                    ).length === 0 &&
                      brandSearch && (
                        <div className="py-4 text-center text-sm text-muted-foreground">
                          {getNoBrandsFoundText(brandSearch)}
                        </div>
                      )}

                    {uniqueBrands.length === 0 && (
                      <div className="py-4 text-center text-sm text-muted-foreground">
                        {translations.messages.noBrandsAvailable}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </TableHead>

        {/* Tags filter */}
        <TableHead key="filter-tags" className="hidden p-2 md:table-cell">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="h-7 w-full justify-start border-input bg-background text-xs font-normal"
                onClick={e => e.stopPropagation()}
              >
                <span className="truncate">
                  {Array.isArray(filters.tags) && filters.tags.length > 0
                    ? i18n.getFiltersMessageText('tagsSelected', {
                        count: filters.tags.length,
                        plural: filters.tags.length === 1 ? '' : 's',
                      })
                    : i18n.getFiltersMessageText('noTagsSelected')}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3" align="start">
              <div className="space-y-2">
                {/* Search input for tags */}
                <Input
                  placeholder={translations.placeholders.searchTags}
                  className="h-8 text-xs"
                  value={tagSearch}
                  onChange={e => setTagSearch(e.target.value)}
                />

                <div className="tag-list max-h-48 overflow-y-auto pr-2">
                  {Array.isArray(uniqueTags) && uniqueTags.length > 0 ? (
                    <div className="space-y-1">
                      {/* Filter tags based on search and limit for performance */}
                      {uniqueTags
                        .filter(tag => tag && tag.toLowerCase().includes(tagSearch.toLowerCase()))
                        .slice(0, 100)
                        .map(tag => (
                          <div key={tag} className="tag-item flex items-center">
                            <Checkbox
                              id={`tag-${tag}`}
                              checked={Array.isArray(filters.tags) && filters.tags.includes(tag)}
                              onCheckedChange={checked => {
                                // Create new tags array
                                const newTags = checked
                                  ? [...(Array.isArray(filters.tags) ? filters.tags : []), tag]
                                  : Array.isArray(filters.tags)
                                    ? filters.tags.filter((t: string) => t !== tag)
                                    : [];

                                onFilterChange('tags', newTags);
                              }}
                            />
                            <Label htmlFor={`tag-${tag}`} className="ml-2 cursor-pointer text-sm">
                              {tag}
                            </Label>
                          </div>
                        ))}
                      {uniqueTags.filter(
                        tag => tag && tag.toLowerCase().includes(tagSearch.toLowerCase())
                      ).length === 0 &&
                        tagSearch && (
                          <div className="py-2 text-center text-xs text-muted-foreground">
                            {getNoTagsFoundText(tagSearch)}
                          </div>
                        )}
                      {uniqueTags.filter(
                        tag => tag && tag.toLowerCase().includes(tagSearch.toLowerCase())
                      ).length > 100 && (
                        <div className="border-t py-2 text-center text-xs text-muted-foreground">
                          {i18n.getFiltersMessageText('showingFirstResults')}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="py-2 text-center text-sm text-muted-foreground">
                      {translations.messages.noTagsAvailable}
                    </div>
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </TableHead>

        {/* GTIN/Barcode filter */}
        <TableHead key="filter-barcode" className="p-2">
          <Input
            placeholder={translations.placeholders.enterBarcode}
            value={filters.barcode ?? ''}
            onChange={e => onFilterChange('barcode', e.target.value)}
            className="h-7 border-input bg-background text-xs font-normal"
            onClick={e => e.stopPropagation()}
          />
        </TableHead>

        {/* Price filter */}
        <TableHead key="filter-price" className="p-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'h-7 text-xs w-full justify-start font-normal border-input bg-background',
                  // Show active state when price filters are applied
                  (filters.minPrice || filters.maxPrice) &&
                    'border-primary bg-primary/10 text-primary'
                )}
                onClick={e => e.stopPropagation()}
              >
                <span>{formatPriceDisplayText()}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-60 p-3" align="start">
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor="price-min" className="text-sm font-medium text-foreground">
                      {i18n.getFiltersDateLabelText('minimum')}
                    </Label>
                    <Input
                      id="price-min"
                      type="text"
                      placeholder={getPricePlaceholder()}
                      className="enterprise-input h-9"
                      value={localMinPrice}
                      onChange={e => setLocalMinPrice(e.target.value)}
                      onBlur={e => handlePriceInputBlur(e.target.value, 'minPrice')}
                      onKeyDown={e => handlePriceInputKeyDown(e, 'minPrice')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="price-max" className="text-sm font-medium text-foreground">
                      {i18n.getFiltersDateLabelText('maximum')}
                    </Label>
                    <Input
                      id="price-max"
                      type="text"
                      placeholder={getPricePlaceholder()}
                      className="enterprise-input h-9"
                      value={localMaxPrice}
                      onChange={e => setLocalMaxPrice(e.target.value)}
                      onBlur={e => handlePriceInputBlur(e.target.value, 'maxPrice')}
                      onKeyDown={e => handlePriceInputKeyDown(e, 'maxPrice')}
                    />
                  </div>
                </div>

                {/* Show validation message if max < min */}
                {isPriceRangeInvalid() && (
                  <div className="rounded-lg bg-red-50 p-2 text-sm text-red-600">
                    {translations.messages.priceRangeInvalid}
                  </div>
                )}

                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    onFilterChange('minPrice', undefined);
                    onFilterChange('maxPrice', undefined);
                  }}
                >
                  {i18n.getFiltersButtonText('clearPriceFilter')}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </TableHead>

        {/* Status filter */}
        <TableHead key="filter-status" className="p-2">
          <Select value={currentStatusValue || 'all'} onValueChange={handleStatusFilterChange}>
            <SelectTrigger className="h-7 border-input bg-background text-xs">
              <SelectValue placeholder={translations.options.allStatuses} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{translations.options.allStatuses}</SelectItem>
              <SelectItem value="true">{translations.options.active}</SelectItem>
              <SelectItem value="false">{translations.options.inactive}</SelectItem>
            </SelectContent>
          </Select>
        </TableHead>

        {/* Created date range */}
        <TableHead key="filter-created_at" className="p-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'h-10 text-sm w-full justify-between font-normal border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
                  (filters.created_at_from || filters.created_at_to) &&
                    'border-primary bg-primary/5 text-primary'
                )}
              >
                <span className="truncate">
                  {filters.created_at_from || filters.created_at_to
                    ? `${formatDateForDisplay(filters.created_at_from) || i18n.getFiltersDateLabelText('start')} - ${formatDateForDisplay(filters.created_at_to) || i18n.getFiltersDateLabelText('end')}`
                    : i18n.getFiltersDateLabelText('selectDateRange')}
                </span>
                <Calendar className="ml-2 size-4 shrink-0 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 border-border bg-popover p-4" align="start">
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label
                      htmlFor="created_at-from"
                      className="text-sm font-medium text-foreground"
                    >
                      {i18n.getFiltersDateLabelText('fromDate')}
                    </Label>
                    <Input
                      id="created_at-from"
                      type="date"
                      className="enterprise-input h-9"
                      value={filters.created_at_from || ''}
                      onChange={e => {
                        const value = e.target.value || undefined;
                        onFilterChange('created_at_from', value);
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="created_at-to" className="text-sm font-medium text-foreground">
                      {i18n.getFiltersDateLabelText('toDate')}
                    </Label>
                    <Input
                      id="created_at-to"
                      type="date"
                      className="enterprise-input h-9"
                      value={filters.created_at_to || ''}
                      onChange={e => {
                        const value = e.target.value || undefined;
                        onFilterChange('created_at_to', value);
                      }}
                    />
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    onFilterChange('created_at_from', '');
                    onFilterChange('created_at_to', '');
                  }}
                >
                  {i18n.getFiltersButtonText('reset')}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </TableHead>

        {/* Updated date range (Last Modified) */}
        <TableHead key="filter-updated_at" className="p-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'h-10 text-sm w-full justify-between font-normal border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
                  (filters.updated_at_from || filters.updated_at_to) &&
                    'border-primary bg-primary/5 text-primary'
                )}
              >
                <span className="truncate">
                  {filters.updated_at_from || filters.updated_at_to
                    ? `${formatDateForDisplay(filters.updated_at_from) || i18n.getFiltersDateLabelText('start')} - ${formatDateForDisplay(filters.updated_at_to) || i18n.getFiltersDateLabelText('end')}`
                    : i18n.getFiltersDateLabelText('selectDateRange')}
                </span>
                <Calendar className="ml-2 size-4 shrink-0 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 border-border bg-popover p-4" align="start">
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label
                      htmlFor="updated_at-from"
                      className="text-sm font-medium text-foreground"
                    >
                      {i18n.getFiltersDateLabelText('fromDate')}
                    </Label>
                    <Input
                      id="updated_at-from"
                      type="date"
                      className="enterprise-input h-9"
                      value={filters.updated_at_from || ''}
                      onChange={e => {
                        const value = e.target.value || undefined;
                        onFilterChange('updated_at_from', value);
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="updated_at-to" className="text-sm font-medium text-foreground">
                      {i18n.getFiltersDateLabelText('toDate')}
                    </Label>
                    <Input
                      id="updated_at-to"
                      type="date"
                      className="enterprise-input h-9"
                      value={filters.updated_at_to || ''}
                      onChange={e => {
                        const value = e.target.value || undefined;
                        onFilterChange('updated_at_to', value);
                      }}
                    />
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    onFilterChange('updated_at_from', '');
                    onFilterChange('updated_at_to', '');
                  }}
                >
                  {i18n.getFiltersButtonText('reset')}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </TableHead>

        {/* Actions column - placeholder cell to cover sticky action column */}
        <TableHead
          key="filter-actions"
          className="sticky right-0 top-9 z-50 min-w-[90px] bg-muted/30 p-2"
        />
      </TableRow>
    </>
  );
}
